import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { GzDialogEvents } from '../../../../ui/gzDialog/component/GzDialog';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormBuildConfig } from '../../../../ui/gzForm/formGroup/types/form-build-config';
import { Subscription } from 'rxjs';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { FormValidator } from '../../../../ui/gzForm/formField/types/form-validator';
import { GzTextareaWebComponent } from '../../../../ui/gzInput';
import { debounce } from '../../../../utils/event.utils';
import { WEB_COMPONENT_GZ_DASHBOARD_FEEDBACK_DIALOG_CONTENT } from '../../../../webcomponents.constants';
import { FeedBackInfo } from '../types';
import { elementClassListAdd, elementClassListRemove } from '../../../../utils/htmlElement.utils';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { FeedbackHelper } from '../../../../utils/feedback/feedback.helper';
import { FeedbackType } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { Feature } from '@nirvana/shared/constants';

/**
 * @summary "Feedback" dialog content
 * @tag gz-dashboard-feedback-dialog-content
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_GZ_DASHBOARD_FEEDBACK_DIALOG_CONTENT)
export class GzDashboardFeedBackDialogContentWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  /**
   * Array of view name validators
   * @type {Array<any>}
   */
  @property({
    attribute: false,
    reflect: false,
    type: Array,
  })
  public validators: Array<FormValidator> = [];

  /**
   * @internal
   * @type {FormGroup}
   * @private
   */
  private formGroup: FormGroup;

  /**
   * @internal
   * @type {GzTextareaWebComponent}
   * @private
   */
  @query('#textareaInput')
  private inputComponent!: GzTextareaWebComponent;

  public hasExtraInfo = false;
  private hasTypeOption = false;
  private fromType = 0;
  private FEEDBACK_TYPES = {
    EASM: 1,
    PHASR: 7
  }

  /**
   * @internal
   * @type {GzFormFieldWebComponent}
   * @private
   */
  @query('#feedBackInput')
  private feedBackInput!: GzFormFieldWebComponent;

  @query('#feedBackUsefulDataOrWidgetMissing')
  private feedBackUsefulDataOrWidgetMissing!: GzFormFieldWebComponent;

  @query('#feedBackWhatDataOrWidgetsAreMissing')
  private feedBackWhatDataOrWidgetsAreMissing!: GzFormFieldWebComponent;

  @query('#feedBackDataBetterOrganized')
  private feedBackDataBetterOrganized!: GzFormFieldWebComponent;

  @query('#feedBackDataInWidgets')
  private feedBackDataInWidgets!: GzFormFieldWebComponent;

  @query('#feedBackUsefulFiltersMissing')
  private feedBackUsefulFiltersMissing!: GzFormFieldWebComponent;

  @query('#feedBackWhatFiltersAreMissing')
  private feedBackWhatFiltersAreMissing!: GzFormFieldWebComponent;

  @query('#feedBackWhatDefaultViewsLikeToSee')
  private feedBackWhatDefaultViewsLikeToSee!: GzFormFieldWebComponent;

  @query('#feedBackNeedMoreCustomizingOptions')
  private feedBackNeedMoreCustomizingOptions!: GzFormFieldWebComponent;

  @query('#feedBackWhatDisplayOptionsAreMissing')
  private feedBackWhatDisplayOptionsAreMissing!: GzFormFieldWebComponent;

  @query('#feedBackWidgetOptions')
  private feedBackWidgetOptions!: GzFormFieldWebComponent;

  @query('#feedBackFullScreenMonitoringMode')
  private feedBackFullScreenMonitoringMode!: GzFormFieldWebComponent;

  @query('#feedBackType')
  private feedBackType!: GzFormFieldWebComponent;

  @query('#feedBackTypeWrapper')
  private feedBackTypeWrapper!: HTMLElement;

  /**
   * @internal
   * @type {GzButtonWebComponent}
   * @private
   */
  @query('#sendButton')
  private sendButton: GzButtonWebComponent;

  /**
   * @internal
   * @type {Subscription | null}
   * @private
   */
  @state()
  private subscription: Subscription | null;

  @property({ type: Boolean, attribute: true, reflect: true })
  complexPopup = false;

  @property({ type: FeedbackType, attribute: true, reflect: true })
  feedbackType = FeedbackType.HEALTH_DASHBOARD;

  public firstUpdated() {
    this.setFeedbackTypeVisibility();
    this.setValidators();
    this.buildConfig();
    this.addSubscription();

    debounce(() => {
      this.inputComponent.inputFocus();
    }, 100);
  }

  render() {
    return html`
      <div class="feedback-form-wrapper">
        <div class="feedback-form-content">${this.renderSimpleForm()} ${this.renderComplexForm()}</div>

        <div class="action-buttons">
          <gz-button type="${ButtonTypes.FULL}" id="sendButton" class="mr-2" @click="${this.handleSendButton}">
            ${translateFn('Submit')}
          </gz-button>
          <gz-button
            type="${ButtonTypes.OUTLINE}"
            color="${ButtonColors.SECONDARY}"
            @click="${this.handleCancelButton}"
          >
            ${translateFn('Cancel')}
          </gz-button>
        </div>
      </div>
    `;
  }

  private renderSimpleForm() {
    return html` <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-2"
        >${translateFn('Your feedback can make a difference!')}
      </gz-text>
      ${this.renderExtraInfo()}
      <gz-form-field label="1. ${translateFn('Your thoughts*')}" id="feedBackInput" class="pt-2 pb-3">
        <gz-textarea
          maxLength="64"
          gz-form-input
          type="text"
          id="textareaInput"
          hasCounter="true"
          maxChars="500"
          resizablebycontent
          placeholder="${translateFn('Type here')}"
        ></gz-textarea>
        <gz-input-error id="inputError"></gz-input-error>
      </gz-form-field>`;
  }

  private renderComplexForm() {
    if (!this.complexPopup) {
      return;
    }

    return html` <gz-text
        fontSize="${FontSizes.HEADING_5}"
        fontWeight="${FontWeights.SEMIBOLD}"
        class="section-title mb-2"
        >${translateFn('Optional feedback')}
      </gz-text>

      <!--Widgets / Data-->
      <gz-text fontSize="${FontSizes.PARAGRAPH_11}" class="text-uppercase mb-2" fontWeight="500"
        >${translateFn('Widgets and Data')}
      </gz-text>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >2. ${translateFn('Are there any useful data or widgets missing from the current dashboard?')}
      </gz-text>
      <gz-form-field id="feedBackUsefulDataOrWidgetMissing" class="mb-1">
        <gz-radio-group gz-form-input class="d-flex">
          <gz-radio value="yes" label="${translateFn('Yes')}" @click="${this.handleRadioClick}"></gz-radio>
          <gz-radio value="no" label="${translateFn('No')}" class="ml-5" @click="${this.handleRadioClick}"></gz-radio>
        </gz-radio-group>
      </gz-form-field>

      <div class="input-field-hidden">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
          >${translateFn('What types of data or widgets would you like to add?')}
        </gz-text>
        <gz-form-field id="feedBackWhatDataOrWidgetsAreMissing" class="mb-2">
          <gz-textarea
            maxLength="64"
            gz-form-input
            type="text"
            class="text-area-input"
            hasCounter="true"
            maxChars="500"
            resizablebycontent
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error id="inputError"></gz-input-error>
        </gz-form-field>
      </div>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >3. ${translateFn('Do you think the data could be better organized?')}
      </gz-text>
      <gz-form-field id="feedBackDataBetterOrganized" class="mb-1">
        <gz-radio-group gz-form-input class="d-flex">
          <gz-radio value="yes" label="${translateFn('Yes')}" @click="${this.handleRadioClick}"></gz-radio>
          <gz-radio value="no" label="${translateFn('No')}" class="ml-5" @click="${this.handleRadioClick}"></gz-radio>
        </gz-radio-group>
      </gz-form-field>

      <div class="input-field-hidden">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
          >${translateFn('How would you like to view the data in widgets?')}
        </gz-text>
        <gz-form-field id="feedBackDataInWidgets" class="mb-3">
          <gz-textarea
            maxLength="64"
            gz-form-input
            type="text"
            class="text-area-input"
            hasCounter="true"
            maxChars="500"
            resizablebycontent
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error id="inputError"></gz-input-error>
        </gz-form-field>
      </div>

      <!--Smart views / filtering-->
      <gz-text fontSize="${FontSizes.PARAGRAPH_11}" class="text-uppercase mb-3" fontWeight="500"
        >${translateFn('Smart Views and Filtering')}
      </gz-text>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >4. ${translateFn('Are there any useful filters missing from the current dashboard?')}
      </gz-text>
      <gz-form-field id="feedBackUsefulFiltersMissing" class="mb-1">
        <gz-radio-group gz-form-input class="d-flex">
          <gz-radio value="yes" label="${translateFn('Yes')}" @click="${this.handleRadioClick}"></gz-radio>
          <gz-radio value="no" label="${translateFn('No')}" class="ml-5" @click="${this.handleRadioClick}"></gz-radio>
        </gz-radio-group>
      </gz-form-field>

      <div class="input-field-hidden">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
          >${translateFn('What filters would you like to add?')}
        </gz-text>
        <gz-form-field id="feedBackWhatFiltersAreMissing" class="mb-2">
          <gz-textarea
            maxLength="64"
            gz-form-input
            type="text"
            class="text-area-input"
            hasCounter="true"
            maxChars="500"
            resizablebycontent
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error id="inputError"></gz-input-error>
        </gz-form-field>
      </div>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >5. ${translateFn('What default views would you like to add in the Standard section?')}
      </gz-text>
      <gz-form-field id="feedBackWhatDefaultViewsLikeToSee" class="mb-3">
        <gz-textarea
          maxLength="64"
          gz-form-input
          type="text"
          class="text-area-input"
          hasCounter="true"
          maxChars="500"
          resizablebycontent
          placeholder="${translateFn('Type here')}"
        ></gz-textarea>
        <gz-input-error id="inputError"></gz-input-error>
      </gz-form-field>

      <!--Customizable display-->
      <gz-text fontSize="${FontSizes.PARAGRAPH_11}" class="text-uppercase mb-3" fontWeight="500"
        >${translateFn('Customizable dashboard')}
      </gz-text>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >6. ${translateFn('Are there any other options that would help you customize the current dashboard?')}
      </gz-text>
      <gz-form-field id="feedBackNeedMoreCustomizingOptions" class="mb-1">
        <gz-radio-group gz-form-input class="d-flex">
          <gz-radio value="yes" label="${translateFn('Yes')}" @click="${this.handleRadioClick}"></gz-radio>
          <gz-radio value="no" label="${translateFn('No')}" class="ml-5" @click="${this.handleRadioClick}"></gz-radio>
        </gz-radio-group>
      </gz-form-field>

      <div class="input-field-hidden">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
          >${translateFn('What options would you like to add?')}
        </gz-text>
        <gz-form-field id="feedBackWhatDisplayOptionsAreMissing" class="mb-2">
          <gz-textarea
            maxLength="64"
            gz-form-input
            type="text"
            class="text-area-input"
            hasCounter="true"
            maxChars="500"
            resizablebycontent
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error id="inputError"></gz-input-error>
        </gz-form-field>
      </div>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >7.
        ${translateFn(
          'What do you think about the current customizing options (moving, resizing, adding and removing widgets)?'
        )}
      </gz-text>
      <gz-form-field id="feedBackWidgetOptions" class="mb-2">
        <gz-textarea
          maxLength="64"
          gz-form-input
          type="text"
          class="text-area-input"
          hasCounter="true"
          maxChars="500"
          resizablebycontent
          placeholder="${translateFn('Type here')}"
        ></gz-textarea>
        <gz-input-error id="inputError"></gz-input-error>
      </gz-form-field>

      <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >8. ${translateFn('What do you think about the full screen mode?')}
      </gz-text>
      <gz-form-field id="feedBackFullScreenMonitoringMode" class="mb-2">
        <gz-textarea
          maxLength="64"
          gz-form-input
          type="text"
          class="text-area-input"
          hasCounter="true"
          maxChars="500"
          resizablebycontent
          placeholder="${translateFn('Type here')}"
        ></gz-textarea>
        <gz-input-error id="inputError"></gz-input-error>
      </gz-form-field>
      <div class="input-field-hidden" id="feedBackTypeWrapper">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1"
        >9. ${translateFn('Choose the early access program for which you would like to submit your feedback*:')}
        </gz-text>
        <gz-form-field id="feedBackType" class="mb-1">
          <gz-radio-group gz-form-input class="d-flex">
            <gz-radio value="${this.FEEDBACK_TYPES.EASM}" label="${translateFn('EASM')}"></gz-radio>
            <gz-radio value="${this.FEEDBACK_TYPES.PHASR}" label="${translateFn('PHASR')}" class="ml-5"></gz-radio>
          </gz-radio-group>
        </gz-form-field>
      </div>`;
  }

  private setFeedbackTypeVisibility() {
    if (this.feedbackType === FeedbackType.ASM_DASHBOARD) {
      const hasPhasr = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PHASR_VISIBLE]);
      const hasEasm = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EASM_VISIBLE]);
      if (hasPhasr && hasEasm) {
        elementClassListRemove(this.feedBackTypeWrapper, 'input-field-hidden');
        this.hasTypeOption = true;
        return;
      }
      if (hasPhasr) {
        this.fromType = this.FEEDBACK_TYPES.PHASR;
      } else {
        this.fromType = this.FEEDBACK_TYPES.EASM;
      }
    }
  }

  private renderExtraInfo() {
    if (!this.complexPopup) {
      return;
    }
    const extraInfo = this.hasExtraInfo
      ? html` <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-4"
          >${translateFn(
            'Data drill-down is one of the key features that is currently in development. We are eager to hear about any additional functionalities you may have in mind.'
          )}
        </gz-text>`
      : html``;

    const sectionTitle = html`
      <gz-text fontSize="${FontSizes.HEADING_5}" fontWeight="${FontWeights.SEMIBOLD}" class="section-title"
        >${translateFn('General feedback')}
      </gz-text>
    `;
    return html`${extraInfo}${sectionTitle}`;
  }

  private setValidators(): void {
    // for the moment only this input is required
    if (this.feedBackInput) {
      this.feedBackInput.Validators = [RequiredValidator(translateFn('This field is required.'))].concat(
        this.validators
      );
    }
    if (this.feedBackWhatDataOrWidgetsAreMissing) {
      this.feedBackWhatDataOrWidgetsAreMissing.Validators = this.validators;
    }
    if (this.feedBackDataInWidgets) {
      this.feedBackDataInWidgets.Validators = this.validators;
    }
    if (this.feedBackWhatFiltersAreMissing) {
      this.feedBackWhatFiltersAreMissing.Validators = this.validators;
    }
    if (this.feedBackWhatDefaultViewsLikeToSee) {
      this.feedBackWhatDefaultViewsLikeToSee.Validators = this.validators;
    }
    if (this.feedBackWhatDisplayOptionsAreMissing) {
      this.feedBackWhatDisplayOptionsAreMissing.Validators = this.validators;
    }
    if (this.feedBackWidgetOptions) {
      this.feedBackWidgetOptions.Validators = this.validators;
    }
    if (this.feedBackFullScreenMonitoringMode) {
      this.feedBackFullScreenMonitoringMode.Validators = this.validators;
    }
    if (this.feedBackType && this.hasTypeOption) {
      this.feedBackType.Validators = [RequiredValidator(translateFn('This field is required.'))].concat(
        this.validators
      );
    }
  }

  private handleRadioClick(Event: {
    target: { value: string; parentElement: { parentElement: { nextElementSibling: HTMLElement } } };
  }): void {
    if (Event.target.value === 'yes') {
      elementClassListRemove(Event.target.parentElement.parentElement.nextElementSibling, 'input-field-hidden');
    } else {
      elementClassListAdd(Event.target.parentElement.parentElement.nextElementSibling, 'input-field-hidden');
    }
  }

  private buildConfig(): void {
    let complexBuildConfig = {};

    if (this.complexPopup) {
      complexBuildConfig = {
        usefulDataOrWidgetMissing: this.feedBackUsefulDataOrWidgetMissing,
        whatDataOrWidgetsAreMissing: this.feedBackWhatDataOrWidgetsAreMissing,
        dataBetterOrganized: this.feedBackDataBetterOrganized,
        dataInWidgets: this.feedBackDataInWidgets,
        usefulFiltersMissing: this.feedBackUsefulFiltersMissing,
        whatFiltersAreMissing: this.feedBackWhatFiltersAreMissing,
        whatDefaultViewsLikeToSee: this.feedBackWhatDefaultViewsLikeToSee,
        needMoreCustomizingOptions: this.feedBackNeedMoreCustomizingOptions,
        whatDisplayOptionsAreMissing: this.feedBackWhatDisplayOptionsAreMissing,
        widgetOptions: this.feedBackWidgetOptions,
        fullScreenMonitoringMode: this.feedBackFullScreenMonitoringMode,
        type: this.feedBackType,
      };
    }

    const config: FormBuildConfig = {
      name: this.feedBackInput,
      ...complexBuildConfig,
    };

    this.formGroup = FormGroup.build(config);
  }

  private addSubscription(): void {
    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.sendButton.disabled = !valid;
      },
    });
  }

  private handleCancelButton(): void {
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CANCEL, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleSendButton(): void {
    const feedbackKeyToForm: Map<keyof FeedBackInfo, string> = new Map([
      ['message', this.feedBackInput.value as string],
    ]);

    if (this.complexPopup) {
      feedbackKeyToForm.set('usefulDataOrWidgetMissing', this.feedBackUsefulDataOrWidgetMissing.value as string);
      feedbackKeyToForm.set('whatDataOrWidgetsAreMissing', this.feedBackWhatDataOrWidgetsAreMissing.value as string);
      feedbackKeyToForm.set('dataBetterOrganized', this.feedBackDataBetterOrganized.value as string);
      feedbackKeyToForm.set('dataInWidgets', this.feedBackDataInWidgets.value as string);
      feedbackKeyToForm.set('usefulFiltersMissing', this.feedBackUsefulFiltersMissing.value as string);
      feedbackKeyToForm.set('whatFiltersAreMissing', this.feedBackWhatFiltersAreMissing.value as string);
      feedbackKeyToForm.set('whatDefaultViewsLikeToSee', this.feedBackWhatDefaultViewsLikeToSee.value as string);
      feedbackKeyToForm.set('needMoreCustomizingOptions', this.feedBackNeedMoreCustomizingOptions.value as string);
      feedbackKeyToForm.set('whatDisplayOptionsAreMissing', this.feedBackWhatDisplayOptionsAreMissing.value as string);
      feedbackKeyToForm.set('widgetOptions', this.feedBackWidgetOptions.value as string);
      feedbackKeyToForm.set('fullScreenMonitoringMode', this.feedBackFullScreenMonitoringMode.value as string);
      feedbackKeyToForm.set('type', this.fromType ? this.fromType.toString() : (this.feedBackType.value as string));
    }

    let feedback: FeedBackInfo = {
      message: '',
      type: '',
      usefulDataOrWidgetMissing: '',
      whatDataOrWidgetsAreMissing: '',
      dataBetterOrganized: '',
      dataInWidgets: '',
      usefulFiltersMissing: '',
      whatFiltersAreMissing: '',
      whatDefaultViewsLikeToSee: '',
      needMoreCustomizingOptions: '',
      whatDisplayOptionsAreMissing: '',
      widgetOptions: '',
      fullScreenMonitoringMode: '',
    };

    feedback = FeedbackHelper.trimFeedbackData<FeedBackInfo>(feedback, feedbackKeyToForm);

    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CONFIRM, {
        bubbles: true,
        composed: true,
        detail: {
          feedback,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-dashboard-feedback-dialog-content': GzDashboardFeedBackDialogContentWebComponent;
  }
}
